import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { RevealLines } from "@/components/Reveal";

export function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.8]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero">
      {/* parallax background */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/a7857c63-2010-4de3-a354-ef80fc69c639/images/a851e0a1c922c21a2c9203e6172e38f843067c56cff58117057d8883e4df9c22.png"
          alt="Data center infrastructure"
          className="w-full h-full object-cover grayscale"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-0 bg-black" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-black via-black/50 to-black/30" />

      <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 lg:px-10 pb-20 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="text-xs uppercase tracking-[0.3em] text-fog mb-8"
          data-testid="hero-eyebrow"
        >
          Technology Systems & Cybersecurity Consulting
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 1 }}
          className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-8 -mt-4"
        >
          <p
            className="font-display text-sm sm:text-base tracking-tight text-paper/80"
            data-testid="hero-safeme-tagline"
          >
            BArecord. Business Captured. Scored. Governed.
          </p>
          <span className="hidden sm:inline text-paper/30">·</span>
          <p
            className="font-display text-sm sm:text-base tracking-tight text-paper/80"
            data-testid="hero-securedesk-tagline"
          >
            SecureDesk Monitoring. Every threat from a single dashboard.
          </p>
        </motion.div>

        <h1 className="font-display text-4xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter text-paper whitespace-nowrap">
          <RevealLines
            lines={["Systems Privacy Data"]}
            delay={0.3}
          />
        </h1>

        <div className="mt-12 flex flex-col lg:flex-row lg:items-end gap-8 lg:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="text-base sm:text-lg text-fog max-w-md leading-relaxed"
            data-testid="hero-subtext"
          >
            Aliwer helps organizations move fast and stay protected — cybersecurity
            assessments, cloud, AI integration, and compliance, delivered by a trusted
            ecosystem of experts.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="flex items-center gap-4"
          >
            <Link
              to="/baseline-review"
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium pl-6 pr-5 py-3.5 hover:bg-white transition-colors"
            >
              Book a $499 Baseline Review
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/exposure-check"
              data-testid="hero-exposure-link"
              className="inline-flex items-center text-sm text-fog hover:text-paper underline underline-offset-4 decoration-white/30 transition-colors"
            >
              Get a free exposure check
            </Link>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-16 font-display text-lg sm:text-xl tracking-tight text-fog"
          data-testid="hero-tagline"
        >
          Secure the systems that run the world.
        </motion.p>
      </div>
    </section>
  );
}
